// TVTool B7a (spec §9.2.3, §9.3, §9.4) — the Help screen's training-video library and its background downloads.
// Reads the farmer's published videos through get_visible_knowledge_assets (useKnowledgeAssets — the same read the
// Knowledge screen makes), keeps the training-tagged rows, groups them (trainingVideos.ts), and on a connection:
// asks for persistent storage once, measures free space, drops cached copies no published video uses any more
// (a newer publish), and fetches the missing ones in the background — the current group first, only the current
// group when space is low (the screen says so). Downloads start when the list loads; nothing waits for a tap.
// Kill switch (§9.5): with the videos off the library is empty and no download is asked for.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/components/AuthContext';
import { useKnowledgeAssets } from '@/shared/hooks/useKnowledgeAssets';
import { groupTrainingAssets, isTrainingAsset, planDownloads, trainingVideosEnabled, type TrainingAsset, type TrainingGroup } from '@/features/help/lib/trainingVideos';
import { cachedTrainingUrls, freeBytes, markPlayed as storeMarkPlayed, persistOnce, playedUrls, precacheTraining, trimTraining } from '@/features/help/lib/trainingCache';

const POLL_MS = 4000;
const POLL_MAX = 45;   // ~3 minutes of watching the cache fill, then stop (a reopen polls again)

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

export interface TrainingLibrary {
  enabled: boolean;
  isLoading: boolean;
  groups: TrainingGroup<TrainingAsset>[];
  cached: Set<string>;
  played: Set<string>;
  lowSpace: boolean;
  online: boolean;
  markPlayed: (url: string) => void;
}

/** @param currentGroup the group the farmer is looking at (null = the first group with videos). */
export function useTrainingLibrary(currentGroup: string | null = null): TrainingLibrary {
  const enabled = trainingVideosEnabled();
  const { user } = useAuth();
  const online = useOnline();
  const q = useKnowledgeAssets();
  const assets = useMemo<TrainingAsset[]>(
    () => (enabled ? ((q.data ?? []) as TrainingAsset[]).filter(isTrainingAsset) : []),
    [q.data, enabled],
  );
  const groups = useMemo(() => groupTrainingAssets(assets), [assets]);
  const [cached, setCached] = useState<Set<string>>(() => new Set());
  const [played, setPlayed] = useState<Set<string>>(() => playedUrls());
  const [lowSpace, setLowSpace] = useState(false);
  const pollRef = useRef<{ timer: number | undefined; left: number }>({ timer: undefined, left: 0 });

  const refreshCached = useCallback(async () => {
    setCached(await cachedTrainingUrls());
  }, []);

  // the cache fills in the background — watch it for a while so the "downloaded" ticks appear without a reload
  const watchCache = useCallback(() => {
    const p = pollRef.current;
    p.left = POLL_MAX;
    if (p.timer !== undefined) return;
    const tick = async () => {
      await refreshCached();
      p.left -= 1;
      if (p.left > 0) p.timer = window.setTimeout(tick, POLL_MS);
      else p.timer = undefined;
    };
    p.timer = window.setTimeout(tick, POLL_MS);
  }, [refreshCached]);

  useEffect(() => { void refreshCached(); }, [refreshCached]);
  useEffect(() => () => { if (pollRef.current.timer !== undefined) clearTimeout(pollRef.current.timer); }, []);

  // §9.3.3 + §9.4: reconcile the cache with the published list whenever the list (re)loads on a connection
  const listKey = assets.map((a) => a.media_url).join('|');
  useEffect(() => {
    if (!enabled || !user?.id || !online || assets.length === 0) return;
    let cancelled = false;
    (async () => {
      await persistOnce();
      const [have, free] = await Promise.all([cachedTrainingUrls(), freeBytes()]);
      if (cancelled) return;
      const group = currentGroup ?? groups[0]?.name ?? null;
      const plan = planDownloads(assets, have, free, group);
      setLowSpace(plan.lowSpace);
      setCached(have);
      if (plan.remove.length > 0) await trimTraining(assets.map((a) => a.media_url!).filter(Boolean));
      if (plan.download.length > 0) {
        await precacheTraining(plan.download.map((a) => ({ url: a.media_url!, bytes: a.media_size_bytes })));
        watchCache();
      }
      console.info('[training] sync', { videos: assets.length, cached: have.size, download: plan.download.length, remove: plan.remove.length, lowSpace: plan.lowSpace });
    })().catch(() => { /* the screen still lists and streams; the next open tries again */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, user?.id, online, listKey, currentGroup]);

  const markPlayed = useCallback((url: string) => {
    storeMarkPlayed(url);
    setPlayed(playedUrls());
  }, []);

  return { enabled, isLoading: enabled && q.isLoading, groups, cached, played, lowSpace, online, markPlayed };
}
